import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Robot, ChatsCircle, ShareNetwork, Lightning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { PageHead } from "@/pages/admin/AdminLayout";

const L = ({ t, children }) => <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#888] block mb-1">{t}</span>{children}</label>;

export function Settings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/settings", f); qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="admin-settings">
      <PageHead title="Settings" sub="// storefront control" />
      <form onSubmit={save} className="max-w-2xl space-y-4">
        <div className="flex flex-wrap gap-8 border border-[#2a2a2a] p-4">
          <label className="flex items-center gap-2 text-xs uppercase tracking-wider"><Switch checked={f.store_open} onCheckedChange={(v) => set("store_open", v)} data-testid="settings-store-open-switch" /> Store open</label>
          <label className="flex items-center gap-2 text-xs uppercase tracking-wider"><Switch checked={f.announcement_enabled} onCheckedChange={(v) => set("announcement_enabled", v)} data-testid="settings-announcement-switch" /> Announcement bar</label>
        </div>
        <L t="Announcement text"><input className="field" value={f.announcement} onChange={(e) => set("announcement", e.target.value)} data-testid="settings-announcement-input" /></L>
        <L t="Hero title"><input className="field" value={f.hero_title} onChange={(e) => set("hero_title", e.target.value)} data-testid="settings-hero-title-input" /></L>
        <L t="Hero subtitle"><textarea rows={2} className="field" value={f.hero_sub} onChange={(e) => set("hero_sub", e.target.value)} /></L>
        <L t="Payment instructions (shown at checkout)"><textarea rows={3} className="field" value={f.payment_instructions} onChange={(e) => set("payment_instructions", e.target.value)} data-testid="settings-payment-input" /></L>
        <div className="grid grid-cols-2 gap-3">
          <L t="Discord invite URL"><input className="field" value={f.discord_url} onChange={(e) => set("discord_url", e.target.value)} data-testid="settings-discord-input" /></L>
          <L t="Support email"><input className="field" value={f.support_email} onChange={(e) => set("support_email", e.target.value)} /></L>
        </div>
        <L t="Card price: USD per 1M GP (0 = card payments off)"><input type="number" step="any" min="0" className="field" value={f.usd_per_million_gp ?? 0} onChange={(e) => set("usd_per_million_gp", parseFloat(e.target.value) || 0)} data-testid="settings-usd-rate-input" /></L>
        <button className="btn-bomb" data-testid="settings-save-button">Save settings</button>
      </form>
    </div>
  );
}

export function Command() {
  const mods = [
    [ChatsCircle, "Auto-talker fleet", "Monitor your private-server auto talkers: status, last message, uptime, start / stop."],
    [ShareNetwork, "Social accounts", "Connect and schedule posts across your social media accounts to advertise new drops."],
    [Lightning, "Auto-delivery webhooks", "Payment webhooks (crypto / PayPal) that mark orders paid and release details with zero clicks."],
    [Robot, "Discord bot", "Announce new listings and sold names to your server automatically."],
  ];
  return (
    <div data-testid="admin-command">
      <PageHead title="Bot Command" sub="// automation hub. modules plug in here" />
      <div className="grid md:grid-cols-2 gap-3">
        {mods.map(([Icon, t, d]) => (
          <div key={t} className="border border-dashed border-[#ff2a55]/40 bg-[#0a0a0a] p-6 relative overflow-hidden">
            <div className="absolute top-3 right-3 text-[10px] tracking-[0.25em] uppercase text-[#ffb020] animate-flicker">Module slot</div>
            <Icon size={34} weight="duotone" className="text-[#ff2a55] mb-4" />
            <div className="font-display text-2xl mb-1">{t}</div>
            <p className="text-sm text-[#999]">{d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
